import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useModalDismiss } from './use-modal-dismiss';
import {
  AdminRequestError,
  getInboundCallSettings,
  updateInboundCallMode,
  type InboundCallMode,
  type InboundCallSettingsResponse,
} from './api';

// What each mode does, in the operator's vocabulary — shown beside the radio so
// the choice never needs the runbook open. Order is the escalation ladder.
const MODES: { mode: InboundCallMode; label: string; dot: string; blurb: string }[] = [
  {
    mode: 'off',
    label: 'Off',
    dot: 'bg-destructive',
    blurb: 'Reject every returned call and record nothing. The panic brake.',
  },
  {
    mode: 'shadow',
    label: 'Shadow',
    dot: 'bg-amber-500',
    blurb: 'Reject returned calls, but resolve and log who called. No SDR ever rings.',
  },
  {
    mode: 'live',
    label: 'Live',
    dot: 'bg-emerald-500',
    blurb: 'Bridge returned calls to a free SDR in the lead’s pool. Callers stop hearing busy.',
  },
];

const dotFor = (mode: InboundCallMode | null) =>
  MODES.find((m) => m.mode === mode)?.dot ?? 'bg-muted-foreground/40';

// The mode the core is serving right now (effective_mode, not the stored row). Read once
// with the console header and thereafter only when a save reports a new one, which is the
// only way it changes from here. A failed read stays unknown rather than reading as `off`
// — opening the editor retries and surfaces the real error.
export function useInboundStatus(): {
  dot: string;
  label: string;
  apply: (mode: InboundCallMode) => void;
} {
  const [mode, setMode] = useState<InboundCallMode | null>(null);

  useEffect(() => {
    let live = true;
    getInboundCallSettings()
      .then((r) => {
        if (live) setMode(r.effective_mode);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return { dot: dotFor(mode), label: mode ?? '…', apply: setMode };
}

export function InboundCallModeModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (mode: InboundCallMode) => void;
}) {
  const [loaded, setLoaded] = useState<InboundCallSettingsResponse | null>(null);
  const [choice, setChoice] = useState<InboundCallMode | null>(null);
  const [status, setStatus] = useState<string>('Loading…');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dialog = useModalDismiss(onClose);

  useEffect(() => {
    let live = true;
    getInboundCallSettings()
      .then((r) => {
        if (!live) return;
        setLoaded(r);
        setChoice(r.settings.mode);
        setStatus('');
      })
      .catch((err: unknown) => {
        if (live) setStatus(err instanceof AdminRequestError ? err.message : 'Failed to load');
      });
    return () => {
      live = false;
    };
  }, []);

  const save = useCallback(() => {
    if (!loaded || !choice || choice === loaded.settings.mode) return;

    // Live is the one direction with a blast radius: from the next returned call,
    // real prospects ring real SDR browsers. Off/shadow need no friction — they
    // are the brakes, and a brake must not ask twice.
    if (
      choice === 'live' &&
      !window.confirm(
        'Switch inbound calls LIVE?\n\nFrom the next returned call, callers are bridged to a free SDR in their pool — real prospects ringing real headsets. Switching back to shadow/off is instant from this same switch. Continue?',
      )
    ) {
      return;
    }

    setSaving(true);
    setError(null);
    updateInboundCallMode(choice)
      .then((r) => onSaved(r.effective_mode))
      .catch((err: unknown) => {
        setError(err instanceof AdminRequestError ? err.message : 'Failed to save');
        setSaving(false);
      });
  }, [loaded, choice, onSaved]);

  const dirty = loaded !== null && choice !== null && choice !== loaded.settings.mode;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        {...dialog}
        aria-label="Inbound calls"
        className="bg-card flex max-h-[90vh] w-[min(28rem,94vw)] flex-col gap-4 overflow-y-auto rounded-lg border p-5 shadow-xl focus:outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold">Inbound calls</h2>
          <p className="text-muted-foreground text-sm">
            What happens when a prospect rings one of our numbers back. Takes effect on the next
            call — no deploy.
          </p>
        </div>

        {status && <p className="text-muted-foreground text-sm">{status}</p>}
        {error && (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        {loaded && (
          <>
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="Inbound call mode">
              {MODES.filter((m) => loaded.modes.includes(m.mode)).map((m) => (
                <label
                  key={m.mode}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                    choice === m.mode && 'border-foreground/40 bg-muted/40',
                  )}
                >
                  <input
                    type="radio"
                    name="inbound-mode"
                    className="mt-1"
                    checked={choice === m.mode}
                    onChange={() => setChoice(m.mode)}
                  />
                  <span>
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <span aria-hidden className={cn('h-2 w-2 rounded-full', m.dot)} />
                      {m.label}
                      {loaded.settings.mode === m.mode && (
                        <span className="text-muted-foreground text-xs font-normal">(current)</span>
                      )}
                    </span>
                    <span className="text-muted-foreground block text-xs leading-relaxed">
                      {m.blurb}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {loaded.effective_mode !== loaded.settings.mode && (
              <div className="bg-muted/40 rounded-md border p-3 text-xs leading-relaxed">
                The serving node is currently dispatching{' '}
                <span className="font-medium">{loaded.effective_mode}</span> while the saved row
                says <span className="font-medium">{loaded.settings.mode}</span> — a cache refresh
                is in flight or a node predates the row. Saving re-broadcasts.
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" onClick={save} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
